import React from "react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, ArrowUpAZ, ArrowDownZA, Filter, Pencil, Trash2, Type as TypeIcon, X, ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

// Output key of a well entry — the column name the query engine returns for it.
export function entryKey(kind, e) {
  if (!e) return null;
  if (kind === "meas") return e.key || e.alias || (e.field ? `${e.agg || "sum"}_${e.field}` : null);
  return e.key || e.alias || (e.field ? (e.date_part && e.date_part !== "none" ? `${e.field}_${e.date_part}` : e.field) : null);
}

export const AS_TYPES = [
  { value: "", label: "Auto (from dataset)" },
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
];

// Aggregations that make sense for a field type (visual-scoped, type-aware).
export function aggsForType(t, all) {
  if (!t || ["number", "currency", "percent", "integer", "float"].includes(t)) return all;
  if (t === "datetime" || t === "date") return all.filter((a) => ["count", "count_distinct", "counta", "min", "max"].includes(a.value));
  return all.filter((a) => ["count", "count_distinct", "counta", "min", "max"].includes(a.value));
}

// Per-field ⋯ menu in the Assigned Data wells: sort / filter / rename / data type / remove.
// Everything it writes lives on THIS widget's config — dataset defaults are never touched.
export function InspectorFieldMenu({ kind, index, entry, config, patchConfig, fieldType, onRename, onRemove, label }) {
  const key = entryKey(kind, entry);
  const sort = (config.sort || [])[0];
  const sortedDir = sort && key && sort.key === key ? (sort.direction || "desc") : null;
  const setSort = (direction) => {
    if (!key) return;
    const rest = (config.sort || []).filter((s) => (s.key || s.field) !== key);
    patchConfig({ sort: sortedDir === direction ? rest : [{ ...(sort && sort.key === key ? sort : {}), key, direction }, ...rest] });
  };
  const hasFilter = !!entry?.field && (config.filters || []).some((f) => f && f.field === entry.field);
  const t = entry?.as_type || (entry?.field && fieldType ? fieldType(entry.field) : "");
  const isNum = ["number", "currency", "percent"].includes(t);
  const isDate = t === "datetime" || t === "date";
  const addFilter = () => {
    if (!entry?.field || hasFilter) return;
    const op = kind === "meas" || isNum ? "gte" : isDate ? "between" : "in";
    patchConfig({ filters: [...(config.filters || []), { field: entry.field, op, value: op === "in" ? [] : "" }] });
  };
  // Reorder inside the well — order drives axis/series order on the chart.
  const move = (delta) => {
    const listKey = kind === "dim" ? "dimensions" : "measures";
    const list = [...(config[listKey] || [])];
    const to = index + delta;
    if (to < 0 || to >= list.length) return;
    [list[index], list[to]] = [list[to], list[index]];
    patchConfig({ [listKey]: list });
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`${kind}-menu-${index}`} title="Field options" className="h-8 w-7 shrink-0 rounded-md flex items-center justify-center text-[color:var(--ink-3)] hover:text-[color:var(--ink)] hover:bg-[color:var(--surface-2)]">
          {sortedDir ? (sortedDir === "asc" ? <ArrowUpAZ className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" /> : <ArrowDownZA className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" />) : <MoreVertical className="w-3.5 h-3.5" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-[10px] text-[color:var(--ink-3)] font-normal truncate">{label || entry?.field || key} · this visual only</DropdownMenuLabel>
        <DropdownMenuItem data-testid={`${kind}-menu-rename-${index}`} onClick={onRename}><Pencil className="w-3.5 h-3.5 mr-2" />Rename for this visual…</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid={`${kind}-menu-sort-asc-${index}`} disabled={!key} onClick={() => setSort("asc")} className={cn(sortedDir === "asc" && "text-[color:var(--brand-ocean)]")}>
          <ArrowUpAZ className="w-3.5 h-3.5 mr-2" />Sort ascending{sortedDir === "asc" ? " ✓" : ""}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`${kind}-menu-sort-desc-${index}`} disabled={!key} onClick={() => setSort("desc")} className={cn(sortedDir === "desc" && "text-[color:var(--brand-ocean)]")}>
          <ArrowDownZA className="w-3.5 h-3.5 mr-2" />Sort descending{sortedDir === "desc" ? " ✓" : ""}
        </DropdownMenuItem>
        {sortedDir && <DropdownMenuItem data-testid={`${kind}-menu-sort-clear-${index}`} onClick={() => patchConfig({ sort: (config.sort || []).filter((s) => (s.key || s.field) !== key) })}><X className="w-3.5 h-3.5 mr-2" />Clear sort</DropdownMenuItem>}
        <DropdownMenuItem data-testid={`${kind}-menu-filter-${index}`} disabled={!entry?.field || hasFilter} onClick={addFilter}>
          <Filter className="w-3.5 h-3.5 mr-2" />{hasFilter ? "Filter already on this visual" : "Add as filter…"}
        </DropdownMenuItem>
        {kind === "dim" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={`dim-menu-up-${index}`} disabled={index === 0} onClick={() => move(-1)}>
              <ChevronUp className="w-3.5 h-3.5 mr-2" />Move up
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`dim-menu-down-${index}`} disabled={index >= ((config.dimensions || []).length - 1)} onClick={() => move(1)}>
              <ChevronDown className="w-3.5 h-3.5 mr-2" />Move down
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid={`dim-menu-type-${index}`}><TypeIcon className="w-3.5 h-3.5 mr-2" />Data type · <span className="ml-1 text-[color:var(--brand-ocean)]">{entry?.as_type || (t ? `auto (${t})` : "auto")}</span></DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {AS_TYPES.map((o) => (
                  <DropdownMenuItem key={o.value || "auto"} data-testid={`dim-menu-type-${o.value || "auto"}-${index}`}
                    onClick={() => patchConfig({ dimensions: (config.dimensions || []).map((d, j) => j === index ? { ...d, as_type: o.value || undefined, ...(o.value !== "date" && !isDate ? { date_part: "none" } : {}) } : d) })}>
                    {o.label}{(entry?.as_type || "") === o.value ? " ✓" : ""}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}
        {kind === "meas" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={`meas-menu-up-${index}`} disabled={index === 0} onClick={() => move(-1)}>
              <ChevronUp className="w-3.5 h-3.5 mr-2" />Move up
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`meas-menu-down-${index}`} disabled={index >= ((config.measures || []).length - 1)} onClick={() => move(1)}>
              <ChevronDown className="w-3.5 h-3.5 mr-2" />Move down
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid={`${kind}-menu-remove-${index}`} onClick={onRemove} className="text-[color:var(--danger)]"><Trash2 className="w-3.5 h-3.5 mr-2" />Remove from visual</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
